/**
 * Create-or-update + test for the in-flow onboarding connect step.
 * Remembers a pending integration id so retries do not create duplicates.
 */

/**
 * @param {(path: string, opts?: object) => Promise<object>} api
 * @param {{
 *   productId: string,
 *   providerId: string,
 *   label?: string,
 *   value: string,
 *   integrationId?: string|null,
 * }} opts
 * @returns {Promise<{
 *   integrationId: string|null,
 *   success: boolean,
 *   error?: string,
 *   createStatus?: number,
 *   createError?: string,
 *   test?: object|null,
 * }>}
 */
export async function saveConnectAttempt(api, opts) {
  const productId = opts && opts.productId;
  const providerId = opts && opts.providerId;
  const value = opts && opts.value;
  const label = (opts && opts.label) || '';
  let integrationId = (opts && opts.integrationId) || null;

  if (!productId || !providerId || !value) {
    return { integrationId, success: false, error: 'Missing product, provider, or credential' };
  }

  let save;
  if (integrationId) {
    save = await api(`/connectors/${encodeURIComponent(integrationId)}`, {
      method: 'PUT',
      body: { label, value },
    });
  } else {
    save = await api('/connectors', {
      method: 'POST',
      body: {
        product_id: productId,
        provider_id: providerId,
        label,
        credential_type: 'api_key',
        value,
      },
    });
  }

  if (!save || save.error || !save.integration || !save.integration.id) {
    const createError = (save && save.error) || 'Could not save this connector';
    return {
      integrationId,
      success: false,
      error: createError,
      createStatus: save && save.status,
      createError,
      test: null,
    };
  }

  integrationId = save.integration.id;
  const test = await api(`/connectors/${encodeURIComponent(integrationId)}/test`, {
    method: 'POST',
  });

  if (!test || test.error) {
    const error = (test && test.error) || (test && test.message) || 'Connection saved but the handshake failed';
    return {
      integrationId,
      success: false,
      error,
      test: test || { error },
    };
  }

  return { integrationId, success: true, test };
}

/**
 * Drop a connector that was created but never fully verified (failed test / cancel).
 * @param {(path: string, opts?: object) => Promise<object>} api
 * @param {string|null|undefined} integrationId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function discardPendingConnect(api, integrationId) {
  if (!integrationId) return { ok: true };
  const result = await api(`/connectors/${encodeURIComponent(integrationId)}`, {
    method: 'DELETE',
  });
  if (!result || result.error) {
    return { ok: false, error: (result && result.error) || 'Could not remove connector' };
  }
  return { ok: true };
}
